import { useCallback, useEffect, useRef, useState } from 'react';
import type { WorkflowCampaignState, WorkflowListItem } from '@easyflow/shared';
import { apiClient } from '@/lib/api';
import { getErrorMessage } from '@/lib/utils';

/** A failed read is not an empty selection. IDs include paused bindings. */
export function useCampaignWorkflowDraft(open: boolean, campaignId?: string) {
  const [state, setState] = useState<WorkflowCampaignState | null>(null);
  const [ids, setIds] = useState<string[]>([]);
  const [added, setAdded] = useState<WorkflowListItem[]>([]);
  const [replacements, setReplacements] = useState<Record<string, string[]>>({});
  const [dirty, setDirty] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  const context = useRef({ open, campaignId });
  context.current = { open, campaignId };

  useEffect(() => {
    let current = true;
    setState(null); setIds([]); setAdded([]); setReplacements({}); setDirty(false); setError(null);
    setLoading(open && !!campaignId);
    if (open && campaignId) {
      void apiClient.getCampaignWorkflows(campaignId).then(result => {
        if (!current || context.current.campaignId !== campaignId || !context.current.open) return;
        setState(result);
        setIds(result.triggers.filter(link => !link.dissociated_at).map(link => link.workflow_id));
      }).catch(error => {
        if (current) setError(getErrorMessage(error));
      }).finally(() => { if (current) setLoading(false); });
    }
    return () => { current = false; };
  }, [open, campaignId, attempt]);

  const ready = open && (!campaignId || state !== null) && !loading && !error;
  const add = (definition: WorkflowListItem) => {
    if (!ready || ids.includes(definition.id) || !definition.campaign_selection?.allowed) return;
    setIds(previous => [...previous, definition.id]);
    setAdded(previous => [...previous, definition]);
    setDirty(true);
  };
  const remove = (id: string) => {
    if (!ready) return;
    setIds(previous => previous.filter(value => value !== id));
    setReplacements(previous => Object.fromEntries(Object.entries(previous).filter(([key]) => key !== id)));
    setDirty(true);
  };
  const replace = (workflowId: string, ruleId: string, selected: boolean) => {
    if (!ready) return;
    setReplacements(previous => {
      const next = Object.fromEntries(Object.entries(previous).map(([id, rules]) => [id, rules.filter(rule => rule !== ruleId)]));
      if (selected) next[workflowId] = [...(next[workflowId] ?? []), ruleId];
      return next;
    });
    setDirty(true);
  };
  return { state, ids, added, replacements, dirty, loading, error, ready, add, remove, replace,
    retry: useCallback(() => setAttempt(value => value + 1), []),
    payload: () => ready && dirty ? { linked_workflow_ids: ids, workflow_replacements: replacements } : {},
  };
}
